import React, { useState, useEffect, useMemo } from 'react';
import notify from '../utils/notify';
import { useApi } from '../useApi';
import RoundSelectionModal from './RoundSelectionModal';
import TeeSelectionModal from './TeeSelectionModal';
import CourseSections from './CourseSections';
import { useNearbyCourses } from '../hooks/useNearbyCourses';
import { courseLocation, courseMeta, localIsoDate } from '../utils/courseDistance';

// singleRound: the tournament is one round only (e.g. advanced competitions) —
// picking a course selects it directly (replacing any previous choice), with
// no round-number step.
const Step2_Courses = ({ data, updateData, isLocked, singleRound = false }) => {
    const [allCourses, setAllCourses] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [teeModalCourse, setTeeModalCourse] = useState(null);
    const { authenticatedFetch } = useApi();

    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const response = await authenticatedFetch('/courses');
                if (response.ok) {
                    const courses = await response.json();
                    setAllCourses(courses);
                } else {
                    console.error("Fetch courses failed with status:", response.status);
                    setAllCourses([]);
                }
            } catch (error) {
                console.error("Failed to fetch courses:", error);
                setAllCourses([]);
            }
        };
        fetchCourses();
    }, [authenticatedFetch]);

    const handleAddClick = (course) => {
        if (course.tees && course.tees.length > 0) {
            setTeeModalCourse(course);
        } else {
            addCourseWithTee(course, null);
        }
    };

    const handleTeeSelect = (tee, teesInPlay) => {
        if (teeModalCourse) {
            addCourseWithTee(teeModalCourse, tee, teesInPlay);
        }
        setTeeModalCourse(null);
    };

    // `teesInPlay` is the set of tees declared for this round. It becomes the
    // menu the per-player tee pickers offer on Assign Groups, so it has to
    // travel with the course through the wizard rather than being derived
    // later from who happens to be assigned to what.
    const addCourseWithTee = (course, tee, teesInPlay = []) => {
        const teeIds = teesInPlay?.length
            ? teesInPlay
            : (tee?.id ? [tee.id] : []);
        const courseWithTee = {
            ...course,
            tee_id: tee?.id || null,
            tee_name: tee?.name || null,
            tees_in_play: teeIds,
        };
        if (data.signup_mode === 'roll_up') {
            if (data.courses.length > 0) {
                notify.warn("Tee Sheet Sign Up is only available for quick round tournaments.");
                return;
            }
            updateData({ courses: [{ ...courseWithTee, sequence_number: 1 }] });
            setSearchTerm('');
        } else if (singleRound) {
            // One round only — the new pick replaces any previous course.
            updateData({ courses: [{ ...courseWithTee, sequence_number: 1 }] });
            setSearchTerm('');
        } else {
            setSelectedCourse(courseWithTee);
            setIsModalOpen(true);
        }
    };

    const handleRoundSelect = (sequenceNumber) => {
        if (!selectedCourse) return;

        const newCourse = { ...selectedCourse, sequence_number: parseInt(sequenceNumber) };
        const nextRoundNumber = data.courses.length + 1;

        if (data.courses.some(c => c.sequence_number === newCourse.sequence_number)) {
            notify.warn(`Error: Round ${newCourse.sequence_number} is already assigned to a course. Please choose a different round number.`);
            return;
        }

        if (newCourse.sequence_number > nextRoundNumber) {
            notify.warn(`Error: Rounds must be added in order. The next round to add is Round ${nextRoundNumber}.`);
            return;
        }

        updateData({ courses: [...data.courses, newCourse] });
        setIsModalOpen(false);
        setSelectedCourse(null);
        setSearchTerm(''); // Clear search after adding
    };

    const handleRemoveCourse = (courseToRemove) => {
        if (courseToRemove.is_started) return; // Only prevent removal of started rounds
        const updatedCourses = data.courses.filter(c =>
            !(c.id === courseToRemove.id && c.sequence_number === courseToRemove.sequence_number)
        );
        updateData({ courses: updatedCourses });
    };

    const hasStartedCourses = data.courses?.some(c => c.is_started);

    // From Round 2 on, "near" means near the round before: a multi-round
    // tournament is planned from home for somewhere else, so the phone is the
    // wrong place to measure from. The latest round already added that has a
    // pin — its own, or the society list's copy (an edited tournament's
    // courses may not carry one).
    const courseAnchor = useMemo(() => {
        if (singleRound || data.signup_mode === 'roll_up') return null;
        const added = [...(data.courses || [])].sort((a, b) => b.sequence_number - a.sequence_number);
        for (const c of added) {
            const at = courseLocation(c) || courseLocation(allCourses.find(x => x.id === c.id));
            if (at) return { ...at, courseId: c.id, name: c.name, round: c.sequence_number };
        }
        return null;
    }, [data.courses, data.signup_mode, singleRound, allCourses]);

    // Near first, then most-played — see utils/courseDistance and courseOrder.
    const { sections, near, miles, adoptDialog } = useNearbyCourses({
        courses: allCourses,
        search: searchTerm,
        today: data.date === localIsoDate(),
        courseAnchor,
        // A directory course just added to the society joins the list and
        // goes straight on to its tee, as its own Add button would.
        onAdopted: (course) => {
            setAllCourses((prev) => (prev.some((c) => c.id === course.id) ? prev : [...prev, course]));
            handleAddClick(course);
        },
    });

    return (
        <div className="wizard-step">
            <h2>{data.signup_mode === 'roll_up' || singleRound ? 'Select Course' : 'Add Courses'}</h2>

            {hasStartedCourses && (
                <div className="wiz-locked-banner">
                    Rounds with scores are locked. You can still add or modify courses for future rounds.
                </div>
            )}

            <div className="item-selector__pills-container">
                <h4>{data.signup_mode === 'roll_up' || singleRound ? 'Selected Course' : 'Selected Courses'}</h4>
                <div className="item-selector__pills item-selector__pills--single-col">
                    {data.courses.sort((a, b) => a.sequence_number - b.sequence_number).map((course) => (
                        <div key={`${course.id}-${course.sequence_number}`} className="pill">
                            <span className="pill__text">
                                {course.is_started && <span title="Scores recorded for this round" style={{marginRight: '5px'}}>🔒</span>}
                                {data.signup_mode === 'roll_up' || singleRound ? course.name : `R${course.sequence_number}: ${course.name}`}
                                {(() => {
                                    // Name every tee in play, not just the main
                                    // one — the whole point of the pill here is
                                    // to show at a glance that this is a
                                    // mixed-tee round.
                                    const ids = course.tees_in_play || [];
                                    if (ids.length > 1) {
                                        const names = ids
                                            .map(id => (course.tees || []).find(t => t.id === id)?.name)
                                            .filter(Boolean);
                                        return <span style={{ fontSize: '0.8em', opacity: 0.7 }}> ({names.join(', ')})</span>;
                                    }
                                    return course.tee_name
                                        ? <span style={{ fontSize: '0.8em', opacity: 0.7 }}> ({course.tee_name})</span>
                                        : null;
                                })()}
                            </span>
                            {!course.is_started && (
                                <button onClick={() => handleRemoveCourse(course)} className="pill__remove-btn">&times;</button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="item-selector__search search-box">
                <svg className="search-box__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"/>
                </svg>
                <input
                    type="text"
                    className="search-box__input"
                    placeholder="Search for courses..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="item-selector__list">
                <CourseSections
                    sections={sections}
                    near={near}
                    renderRow={(entry) => {
                        const { course } = entry;
                        // Only where there is something to report.
                        const meta = courseMeta(entry, { miles });
                        return (
                            <div key={course.id} className="item-selector__list-row">
                                <span className="item-selector__item-name">
                                    {course.name}
                                    {meta && <span className="item-selector__item-meta">{meta}</span>}
                                </span>
                                <button
                                    onClick={() => handleAddClick(course)}
                                    className="btn-toggle-add"
                                    disabled={data.signup_mode === 'roll_up' && data.courses.length > 0}
                                >
                                    Add
                                </button>
                            </div>
                        );
                    }}
                />
            </div>

            <TeeSelectionModal
                isOpen={!!teeModalCourse}
                course={teeModalCourse}
                onSelect={handleTeeSelect}
                onClose={() => setTeeModalCourse(null)}
            />

            {adoptDialog}

            <RoundSelectionModal
                isOpen={isModalOpen}
                onSelect={handleRoundSelect}
                onClose={() => setIsModalOpen(false)}
                courseName={selectedCourse?.name}
            />
        </div>
    );
};

export default Step2_Courses;
